import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertTriangle, X, FileText, CheckCircle2, Presentation } from 'lucide-react';

/**
 * PPTDisclaimer - Modal shown when user uploads a PowerPoint file
 * Shows warning about PPT parsing limitations
 */
export default function PPTDisclaimer({ 
  isOpen, 
  fileName, 
  onProceed, 
  onCancel,
  uploadContext = "translation"
}) {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="ppt-disclaimer-modal"
    >
      <Card className="w-full max-w-lg bg-white relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Warning gradient header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Presentation className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">PowerPoint Upload Notice</h3>
            <p className="text-sm text-orange-100">Please read before proceeding</p>
          </div>
          <button 
            onClick={onCancel}
            className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="ppt-disclaimer-close"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-6">
          {/* File name display */}
          <div className="flex items-center gap-3 bg-zinc-50 rounded-lg p-3 mb-4">
            <FileText className="h-5 w-5 text-orange-500" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500">Selected file</p>
              <p className="text-sm font-medium text-zinc-900 truncate">{fileName}</p>
            </div>
          </div>

          {/* What works section */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              What Works:
            </h4>
            <ul className="space-y-1.5 text-sm text-zinc-600 ml-6">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Text content from all slides</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Bullet points and numbered lists</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Slide titles and subtitles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Speaker notes (if present)</span>
              </li>
            </ul>
          </div>

          {/* Limitations section */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Limitations:
            </h4>
            <ul className="space-y-1.5 text-sm text-zinc-600 ml-6">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>Slide layouts may not preserve perfectly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>Animations and transitions will be lost</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>Images and charts are not translated (text only)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>Complex SmartArt may need manual adjustment</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>Custom fonts may change in export</span>
              </li>
            </ul>
          </div>

          {/* Recommendation box */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Recommendation:</strong> For presentations with heavy graphics, consider 
              translating only the text content and manually updating the original presentation afterward.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1 gap-2"
              data-testid="ppt-disclaimer-cancel"
            >
              <X className="h-4 w-4" />
              Cancel & Choose Another
            </Button>
            <Button
              onClick={onProceed}
              className="flex-1 gap-2 bg-orange-600 hover:bg-orange-700"
              data-testid="ppt-disclaimer-proceed"
            >
              <CheckCircle2 className="h-4 w-4" />
              I Understand, Proceed
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
